import { FC, useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { Delete } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import Navbar from "@/components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "@/shared/components/header-items/HeaderItems";
import DashboardContentLayout from "@/shared/layouts/DashboardContentLayout";
import FieldRow from "@/shared/components/FieldRow";
import useUnsavedChangesBlocker from "@/shared/hooks/useUnsavedChangesBlocker";
import { generateRoute } from "ui";
import { AdRoutes } from "@/constants/ad-routes";
import useEditLeadForm from "../hooks/useEditLeadForm";
import useDeleteLead from "../hooks/useDeleteLead";
import LeadForm from "../components/LeadForm";

const SOURCE_LABELS: Record<"manual" | "inquiry_form", string> = {
  manual: "Manual",
  inquiry_form: "Inquiry Form",
};

const EditLeadPage: FC = () => {
  const navigate = useNavigate();
  const { leadId = "" } = useParams();
  // True once we intentionally leave (save or delete) — disables the
  // unsaved-changes blocker for our own redirect.
  const [leaving, setLeaving] = useState(false);

  const { leadForm, lead, isLoading, isSaving } = useEditLeadForm(
    leadId,
    () => {
      toast.success("Lead updated successfully");
      setLeaving(true);
    },
  );

  const { confirmDeleteLead } = useDeleteLead({
    onDeleted: () => setLeaving(true),
  });

  useUnsavedChangesBlocker(leadForm.dirty && !leaving);

  useEffect(() => {
    if (leaving) {
      navigate(AdRoutes.leads);
    }
  }, [leaving, navigate]);

  const subtitle = lead
    ? `Created ${DateTime.fromISO(lead.createdAt).toFormat("MM/dd/yyyy")} via ${
        SOURCE_LABELS[lead.source]
      }`
    : undefined;

  const onConvertToCustomer = () => {
    navigate(generateRoute(AdRoutes.leadConvert, { leadId }));
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              { label: "Leads", url: AdRoutes.leads },
              { label: "Edit Lead" },
            ]}
          />
          <HeaderItemsRight>
            <Button variant="outlined" onClick={onConvertToCustomer}>
              Convert to Customer
            </Button>
            <Button
              variant="contained"
              disabled={isSaving || !leadForm.dirty || !leadForm.isValid}
              onClick={() => leadForm.submitForm()}
              data-testid="save-lead-changes"
            >
              Save Changes
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: "40px" }}>
          <CircularProgress />
        </Box>
      ) : (
        <LeadForm
          leadForm={leadForm}
          leadInfoSubtitle={subtitle}
          leadInfoFooter={
            // Match the Figma layout: "Delete Lead" in the label column, the
            // description + Delete button in the input column (like the fields
            // above), rather than flush against the form's left edge.
            <FieldRow label="Delete Lead">
              <Typography variant="caption" component="p" mb="16px">
                Are you sure you want to delete {lead?.fullName}?
                <br />
                This will permanently remove all related information, and cannot
                be undone.
              </Typography>
              <Button
                color="error"
                variant="outlined"
                startIcon={<Delete />}
                sx={{ maxWidth: "150px" }}
                onClick={() =>
                  confirmDeleteLead(leadId, lead?.fullName ?? "this lead")
                }
                data-testid="delete-lead"
              >
                Delete
              </Button>
            </FieldRow>
          }
        />
      )}
    </DashboardContentLayout>
  );
};

export default EditLeadPage;
